import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap, useMapEvents, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useNavigate } from 'react-router-dom';
import { BadgeCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fix leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom icons with better visibility
// Inline SVG icons - no external HTTP requests, instant loading on mobile
const createIcon = (color) => new L.DivIcon({
  className: '',
  html: `<div style="width:28px;height:28px;border-radius:50%;background:${color};border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.35);"></div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  popupAnchor: [0, -16],
});

// User location icon - distinct yellow/gold
const userLocationIcon = new L.DivIcon({
  className: 'user-location-marker',
  html: `
    <div style="
      width: 24px;
      height: 24px;
      background: linear-gradient(135deg, #FFD700, #FFA500);
      border: 3px solid white;
      border-radius: 50%;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3), 0 0 0 8px rgba(255,215,0,0.2);
      animation: pulse 2s infinite;
    "></div>
  `,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const blueIcon = createIcon('#3b82f6');
const greenIcon = createIcon('#22c55e');
const redIcon = createIcon('#ef4444');
const orangeIcon = createIcon('#f97316');
const violetIcon = createIcon('#8b5cf6');
const goldIcon = createIcon('#eab308');

const categoryIcons = {
  yoga: violetIcon,
  renforcement: redIcon,
  mobilite: greenIcon,
  outdoor: blueIcon,
  cardio: redIcon,
  pilates: orangeIcon,
  boxe: redIcon,
  danse: violetIcon,
};

// Category colors for legend
const categoryColors = {
  yoga: '#9333ea',
  renforcement: '#ef4444',
  mobilite: '#22c55e',
  outdoor: '#3b82f6',
  cardio: '#ef4444',
  pilates: '#f97316',
  boxe: '#ef4444',
  danse: '#9333ea',
};

// Component to handle user location with radius circle
function LocationMarker({ onLocationFound, showRadius = false, radiusKm = 2 }) {
  const [position, setPosition] = useState(null);
  const map = useMapEvents({
    locationfound(e) {
      setPosition(e.latlng);
      map.flyTo(e.latlng, 14);
      // Save to localStorage for future fallback
      localStorage.setItem('motivya_user_lat', e.latlng.lat.toString());
      localStorage.setItem('motivya_user_lng', e.latlng.lng.toString());
      if (onLocationFound) onLocationFound(e.latlng);
    },
    locationerror(e) {
      console.warn('Geolocation error:', e.message);
      // Fallback: use saved location from localStorage
      const savedLat = localStorage.getItem('motivya_user_lat');
      const savedLng = localStorage.getItem('motivya_user_lng');
      if (savedLat && savedLng) {
        const fallback = { lat: parseFloat(savedLat), lng: parseFloat(savedLng) };
        setPosition(fallback);
        map.flyTo(fallback, 13);
        if (onLocationFound) onLocationFound(fallback);
      }
    },
  });

  useEffect(() => {
    map.locate({ 
      enableHighAccuracy: true, 
      timeout: 10000,
      maximumAge: 300000
    });
  }, [map]);

  return position === null ? null : (
    <>
      {/* Radius circle - always visible with clear styling */}
      {showRadius && (
        <Circle
          center={position}
          radius={radiusKm * 1000} // Convert km to meters
          pathOptions={{
            color: '#3BADE5',
            fillColor: '#3BADE5',
            fillOpacity: 0.15,
            weight: 4,
            dashArray: '12, 8',
          }}
        />
      )}
      {/* User marker */}
      <Marker position={position} icon={userLocationIcon}>
        <Popup>
          <div className="text-center p-1">
            <strong>📍 Vous êtes ici</strong>
            {showRadius && (
              <p className="text-xs text-gray-500 mt-1">Rayon de recherche : {radiusKm} km</p>
            )}
          </div>
        </Popup>
      </Marker>
    </>
  );
}

// Component to show radius around a specific center (for search)
function SearchRadiusCircle({ center, radiusKm = 2 }) {
  if (!center) return null;
  
  return (
    <Circle
      center={center}
      radius={radiusKm * 1000}
      pathOptions={{
        color: '#3BADE5',
        fillColor: '#3BADE5',
        fillOpacity: 0.15,
        weight: 4,
        dashArray: '12, 8',
      }}
    />
  );
}

// Component to update map view
function ChangeView({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, zoom);
    }
  }, [center, zoom, map]);
  return null;
}

// Draggable marker for session creation
function DraggableMarker({ position, onPositionChange }) {
  const markerRef = useRef(null);
  
  const eventHandlers = {
    dragend() {
      const marker = markerRef.current;
      if (marker != null) {
        const latlng = marker.getLatLng();
        onPositionChange(latlng);
      }
    },
  };

  return (
    <Marker
      draggable={true}
      eventHandlers={eventHandlers}
      position={position}
      ref={markerRef}
      icon={blueIcon}
    >
      <Popup>Faites glisser pour positionner la séance</Popup>
    </Marker>
  );
}

// Click handler for map
function MapClickHandler({ onClick }) {
  useMapEvents({
    click(e) {
      onClick(e.latlng);
    },
  });
  return null;
}

// Component to re-trigger geolocation from inside the map
function LocateButton() {
  const map = useMap();
  const [locating, setLocating] = useState(false);
  
  const handleLocate = () => {
    setLocating(true);
    map.once('locationfound', () => setLocating(false));
    map.once('locationerror', () => setLocating(false));
    map.locate({ 
      enableHighAccuracy: true, 
      timeout: 10000,
      maximumAge: 0 
    });
  };
  
  return (
    <div 
      className="leaflet-control" 
      style={{ position: 'absolute', bottom: '80px', right: '12px', zIndex: 1000 }}
    >
      <button
        onClick={handleLocate}
        data-testid="locate-me-btn"
        title="Me localiser"
        style={{
          width: '40px',
          height: '40px',
          background: 'white',
          border: '2px solid rgba(0,0,0,0.2)',
          borderRadius: '4px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
        }}
      >
        {locating ? (
          <span style={{ fontSize: '18px', animation: 'spin 1s linear infinite' }}>⟳</span>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2">
            <circle cx="12" cy="12" r="3"/>
            <path d="M12 2v4M12 18v4M2 12h4M18 12h4"/>
          </svg>
        )}
      </button>
    </div>
  );
}

// Main Map Component for viewing sessions
export function SessionsMap({ 
  sessions = [], 
  onSessionClick,
  onSessionSelect, // New: callback to open side panel instead of navigating
  className = '',
  showUserLocation = true,
  showRadius = false,
  radiusKm = 2,
  center = [50.8503, 4.3517],
  searchCenter = null, // New: center for search radius circle
  zoom = 12,
  mapStyle = 'light' // 'light', 'dark', 'streets'
}) {
  const navigate = useNavigate();
  const [userLocation, setUserLocation] = useState(null);

  const handleSessionClick = (sessionId, session) => {
    if (onSessionSelect && session) {
      // Open side panel with session details
      onSessionSelect(session);
    } else if (onSessionClick) {
      onSessionClick(sessionId);
    } else {
      navigate(`/session/${sessionId}`);
    }
  };

  // Map tile styles
  const tileUrls = {
    light: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    dark: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    streets: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  };

  return (
    <div className={`relative ${className}`} style={{ minHeight: '400px' }}>
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: '100%', width: '100%', borderRadius: '0.75rem' }}
        scrollWheelZoom={true}
        preferCanvas={true}
      >
        {/* Light theme map tiles for better readability */}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url={tileUrls[mapStyle]}
        />
        
        {/* Search radius circle when searching by location */}
        {searchCenter && (
          <SearchRadiusCircle center={searchCenter} radiusKm={radiusKm} />
        )}
        
        {/* Always show radius circle - around user location or map center */}
        {showRadius && !searchCenter && (
          <Circle
            center={userLocation ? [userLocation.lat, userLocation.lng] : center}
            radius={radiusKm * 1000}
            pathOptions={{
              color: '#3BADE5',
              fillColor: '#3BADE5',
              fillOpacity: 0.08,
              weight: 2,
              dashArray: '10, 6',
            }}
          />
        )}
        
        {showUserLocation && (
          <LocationMarker 
            onLocationFound={setUserLocation} 
            showRadius={false}
            radiusKm={radiusKm}
          />
        )}
        
        {/* Locate me button */}
        {showUserLocation && <LocateButton />}
        
        {sessions.map((session) => (
          <Marker
            key={session.session_id}
            position={[session.latitude, session.longitude]}
            icon={categoryIcons[session.category] || blueIcon}
            eventHandlers={{
              click: () => handleSessionClick(session.session_id, session),
            }}
          >
            {/* Tooltip on hover - shows title, date, time, price */}
            <Tooltip 
              direction="top" 
              offset={[0, -35]} 
              opacity={0.95}
              className="custom-tooltip"
            >
              <div className="text-center min-w-[180px]">
                <div className="font-bold text-sm text-gray-900 mb-1">{session.title}</div>
                <div className="text-xs text-gray-500 mb-0.5">
                  {new Date(session.date).toLocaleDateString('fr-FR', { 
                    weekday: 'short', 
                    day: 'numeric', 
                    month: 'short' 
                  })}
                </div>
                <div className="text-xs text-gray-600">{session.start_time} • <span className="font-bold text-[#3BADE5]">{session.price}€</span></div>
              </div>
            </Tooltip>
            
            {/* Only show popup if onSessionSelect is not provided (no side panel mode) */}
            {!onSessionSelect && (
              <Popup>
                <div className="p-2 min-w-[220px]">
                  {/* Category badge */}
                  <div className="flex items-center justify-between mb-2">
                    <span 
                      className="inline-block px-2 py-0.5 rounded text-xs font-semibold text-white"
                      style={{ backgroundColor: categoryColors[session.category] || '#3b82f6' }}
                    >
                      {session.category}
                    </span>
                    {session.spots_left !== undefined && (
                      <span className={`text-xs font-medium ${session.spots_left > 0 ? 'text-green-600' : 'text-red-500'}`}>
                        {session.spots_left > 0 ? `${session.spots_left} places` : 'Complet'}
                      </span>
                    )}
                  </div>
                  
                  {/* Title */}
                  <h3 className="font-bold text-base mb-1 text-gray-900">{session.title}</h3>
                  
                  {/* Coach with verified badge */}
                  {session.coach_name && (
                    <div className="flex items-center gap-1 text-sm text-gray-600 mb-1">
                      <span>{session.coach_name}</span>
                      {session.coach_verified && (
                        <span className="text-blue-500" title="Coach vérifié">✓</span>
                      )}
                    </div>
                  )}
                  
                  {/* Location */}
                  <p className="text-gray-500 text-sm mb-2 flex items-center gap-1">
                    <span>📍</span> {session.location}
                  </p>
                  
                  {/* Date, time, price */}
                  <div className="flex justify-between items-center text-sm border-t pt-2 mt-2">
                    <span className="text-gray-600">
                      {session.date} • {session.start_time}
                    </span>
                    <span className="font-bold text-lg text-[#3BADE5]">{session.price}€</span>
                  </div>
                  
                  {/* CTA Button */}
                  <button 
                    onClick={() => handleSessionClick(session.session_id)}
                    className="mt-3 w-full py-2 bg-[#3BADE5] text-white text-sm font-semibold rounded-lg hover:bg-[#2d9ad4] transition-colors"
                  >
                    Voir détails →
                  </button>
                </div>
              </Popup>
            )}
          </Marker>
        ))}

        {userLocation && <ChangeView center={[userLocation.lat, userLocation.lng]} zoom={14} />}
      </MapContainer>
      
      {/* Session count badge - positioned top-right to avoid zoom controls */}
      <div className="absolute top-4 right-4 z-[1000] bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg border border-gray-200">
        <span className="text-sm font-semibold text-gray-700">
          <span className="text-[#3BADE5] font-bold">{sessions.length}</span> activités
        </span>
      </div>
      
      {/* Radius indicator badge - bottom right above locate button */}
      {showRadius && (
        <div data-testid="radius-badge" className="absolute bottom-[130px] right-3 z-[1000] bg-white/95 backdrop-blur-sm px-2.5 py-1.5 rounded-lg shadow-lg border border-gray-200">
          <span className="text-xs font-semibold text-[#3BADE5] flex items-center gap-1">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="12" cy="12" r="10"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
            {radiusKm} km
          </span>
        </div>
      )}

      {/* Legend - bottom left */}
      <div className="absolute bottom-4 left-4 z-[1000] bg-white/95 backdrop-blur-sm px-3 py-2 rounded-lg shadow-lg border border-gray-200 text-xs">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-gradient-to-br from-yellow-400 to-orange-400 border-2 border-white shadow"></span>
            <span className="text-gray-600">Vous</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-violet-500"></span>
            <span className="text-gray-600">Yoga</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-red-500"></span>
            <span className="text-gray-600">Cardio</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-blue-500"></span>
            <span className="text-gray-600">Outdoor</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-green-500"></span>
            <span className="text-gray-600">Mobilité</span>
          </span>
        </div>
      </div>
    </div>
  );
}

// Map Component for creating/editing sessions (with draggable marker)
export function LocationPickerMap({
  position,
  onPositionChange,
  className = '',
  zoom = 14
}) {
  const handleMapClick = (latlng) => {
    onPositionChange({ lat: latlng.lat, lng: latlng.lng });
  };

  return (
    <div className={`relative ${className}`} style={{ minHeight: '300px' }}>
      <MapContainer
        center={[position.lat, position.lng]}
        zoom={zoom}
        style={{ height: '100%', width: '100%', borderRadius: '0.75rem' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
        />
        
        <DraggableMarker 
          position={[position.lat, position.lng]} 
          onPositionChange={(latlng) => onPositionChange({ lat: latlng.lat, lng: latlng.lng })}
        />
        
        <MapClickHandler onClick={handleMapClick} />
        <ChangeView center={[position.lat, position.lng]} zoom={zoom} />
      </MapContainer>
    </div>
  );
}

// Hero Map for landing page with geolocation
export function HeroMap({ onSessionClick, className = '' }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sessions?page_size=200`);
      if (response.ok) {
        const data = await response.json();
        // Handle paginated response
        setSessions(data.items || data);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSessionClick = (sessionId) => {
    if (onSessionClick) {
      onSessionClick(sessionId);
    } else {
      navigate(`/session/${sessionId}`);
    }
  };

  if (loading) {
    return <div className={`bg-background ${className}`}></div>;
  }

  return (
    <div className={`relative ${className}`}>
      <MapContainer
        center={[50.8503, 4.3517]}
        zoom={12}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
        zoomControl={false}
        dragging={true}
        preferCanvas={true}
      >
        {/* Light theme for better visibility */}
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        />
        
        <LocationMarker showRadius={false} />
        
        {sessions.map((session) => (
          <Marker
            key={session.session_id}
            position={[session.latitude, session.longitude]}
            icon={categoryIcons[session.category] || blueIcon}
            eventHandlers={{
              click: () => handleSessionClick(session.session_id),
            }}
          >
            {/* Tooltip on hover */}
            <Tooltip 
              direction="top" 
              offset={[0, -35]} 
              opacity={0.95}
            >
              <div className="text-center min-w-[140px]">
                <div className="font-bold text-sm text-gray-900">{session.title}</div>
                <div className="text-xs text-gray-600">{session.start_time} • <span className="font-bold text-[#3BADE5]">{session.price}€</span></div>
              </div>
            </Tooltip>
            
            <Popup>
              <div className="p-2 min-w-[180px]">
                <span 
                  className="inline-block px-2 py-0.5 rounded text-xs font-semibold text-white mb-2"
                  style={{ backgroundColor: categoryColors[session.category] || '#3b82f6' }}
                >
                  {session.category}
                </span>
                <h3 className="font-bold text-gray-900">{session.title}</h3>
                <p className="text-sm text-gray-500">{session.location}</p>
                <div className="flex justify-between items-center mt-2 pt-2 border-t">
                  <span className="font-bold text-[#3BADE5]">{session.price}€</span>
                  <button 
                    onClick={() => handleSessionClick(session.session_id)}
                    className="text-xs text-[#3BADE5] font-medium hover:underline"
                  >
                    Voir →
                  </button>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {/* Floating legend for landing page */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-[1000] bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg border border-gray-200">
        <div className="flex items-center gap-4 text-xs">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-violet-500"></span>
            <span className="text-gray-600">Yoga</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>
            <span className="text-gray-600">Cardio</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
            <span className="text-gray-600">Outdoor</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>
            <span className="text-gray-600">Mobilité</span>
          </span>
          <span className="font-semibold text-gray-700 ml-2 pl-2 border-l border-gray-300">
            {sessions.length} activités
          </span>
        </div>
      </div>
    </div>
  );
}

export default SessionsMap;
